/**
 * Centralized browser-storage keys. Namespacing every key avoids collisions
 * with other apps on the same origin and makes a global "sign out" cleanup
 * trivial and exhaustive.
 */
const NAMESPACE = 'hexaunity';

export const STORAGE_KEYS = {
  REMEMBERED_LOGIN: `${NAMESPACE}.remembered-login`,
  THEME: `${NAMESPACE}.theme`,
  SIDEBAR: `${NAMESPACE}.sidebar`,
  REPORTS_SIDEBAR: `${NAMESPACE}.reports-sidebar`,
  /** Saved account metadata (never tokens or passwords). */
  ACCOUNTS: `${NAMESPACE}.accounts`,
  /** `user.id` of the account whose session is currently active. */
  ACTIVE_ACCOUNT: `${NAMESPACE}.active-account`,
  INVOICING_SIDEBAR: `${NAMESPACE}.invoicing-sidebar`,
  FINANCE_SIDEBAR: `${NAMESPACE}.finance-sidebar`,
  ACCOUNTING_SIDEBAR: `${NAMESPACE}.accounting-sidebar`,
  TREASURY_SIDEBAR: `${NAMESPACE}.treasury-sidebar`,
  RECEIVABLES_SIDEBAR: `${NAMESPACE}.receivables-sidebar`,
  /** Expand state of the remaining sidebar groups, one key per group. */
  LEAVE_SIDEBAR: `${NAMESPACE}.leave-sidebar`,
  PAYROLL_SIDEBAR: `${NAMESPACE}.payroll-sidebar`,
  PROJECTS_SIDEBAR: `${NAMESPACE}.projects-sidebar`,
  INTEGRATIONS_SIDEBAR: `${NAMESPACE}.integrations-sidebar`,
  ASSETS_SIDEBAR: `${NAMESPACE}.assets-sidebar`,
  VENDORS_SIDEBAR: `${NAMESPACE}.vendors-sidebar`,
  ACCESS_SIDEBAR: `${NAMESPACE}.access-sidebar`,
  CLIENTS_SIDEBAR: `${NAMESPACE}.clients-sidebar`,
  /**
   * `companies.id` of the entity the user is currently operating as.
   *
   * Per ACCOUNT, not global: two accounts saved in this browser must not
   * inherit each other's active entity, so the key is suffixed with the user
   * id (see `company.store.ts`). Saved so a reload comes back to the same
   * entity rather than silently switching the one invoices are issued from.
   */
  ACTIVE_COMPANY: `${NAMESPACE}.active-company`,
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/**
 * Key prefixes of entries that hold sensitive, session-bound data (the
 * invoice preview hand-off carries the unmasked bank account it prints).
 * Removed whenever a session ends in this browser — sign-out, expiry, account
 * removal, sign-out in another tab — so they never outlive the session that
 * wrote them.
 */
export const SESSION_SCOPED_STORAGE_PREFIXES = ['synkrama.invoice-preview.'] as const;

/**
 * Prefix of the Create Company wizard's draft, one entry per account
 * (`<prefix><user id>`) — finance-web `companies/wizard/draft.ts`.
 */
export const COMPANY_WIZARD_DRAFT_PREFIX = `${NAMESPACE}.company-wizard-draft.`;

/**
 * Fields blanked, at any depth, inside the entries under these prefixes when
 * a session ends (the same moments as SESSION_SCOPED_STORAGE_PREFIXES). The
 * entry itself is kept: a company draft survives sign-out, the bank account
 * numbers typed into it do not.
 */
export const SESSION_SCOPED_STORAGE_FIELDS: Readonly<Record<string, readonly string[]>> = {
  [COMPANY_WIZARD_DRAFT_PREFIX]: ['account_number', 'iban'],
};
